import React from "react";
import { Icon, Flyout, FormGroup, InputText, Button, PasswordInput } from "@accessdev/arc-ui-react";
import { useSelector, useDispatch } from "react-redux";
import { Creators } from "@ReduxStore/actionTypes";
import { selectors } from "@ReduxStore/reducers";
import { signUpSteps, errorLocations } from "../../authHelpers";
import { Member, PASSWORD_MIN_STR } from "@Src/constants";
import { Trans } from "@lingui/react/macro";
import { t } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { useLocation } from "react-router";
import Captcha from "../Captcha";

function Email(props) {
  const { _ } = useLingui();
  const domainsRef = React.useRef(null);
  const { state: locationState } = useLocation();
  const signUpStep = useSelector(selectors.getSignUpStep);
  const registrationStatus = useSelector(selectors.getRegistrationStatus);
  const dispatch = useDispatch();
  const isAdminCompleteProfile = locationState && locationState.isAdminCompleteProfile;
  const isCompleteProfileClass =
    isAdminCompleteProfile || props.isAdminCompleteProfile ? "is-complete-profile" : "";
  const handleNextStep = React.useCallback(() => {
    if (!props.isCompleteProfile) {
      if (props.registrationType === Member.REGISTER_WITH_CODE) {
        dispatch(Creators.setSignUpStep(signUpSteps.REGISTRATION_CODE));
        return;
      }
      if (props.registrationType === Member.REGISTER_WITH_MEMBER_KEY) {
        dispatch(Creators.setSignUpStep(signUpSteps.MEMBER_ID));
        return;
      }
    }
    dispatch(Creators.setSignUpStep(signUpSteps.NAME));
  }, [props.isCompleteProfile, props.registrationType, dispatch]);

  React.useEffect(() => {
    if (registrationStatus === Member.NEEDS_SHARED_SECRET) {
      handleNextStep();
    }

    if (registrationStatus === Member.EMAIL_IS_AVAILABLE) {
      !props.isPhone ? props.completeSignUp() : handleNextStep();
    }
  }, [registrationStatus, handleNextStep, props, dispatch]);

  // Set step if the email is prepopulated
  React.useEffect(() => {
    if (Boolean(props.values.email) && signUpStep !== signUpSteps.EMAIL) {
      dispatch(Creators.setSignUpStep(signUpSteps.EMAIL));
    }
  }, [dispatch, props.values.email, signUpStep]);

  const onSubmit = (event) => {
    if (!isAdminCompleteProfile) {
      let emailValue =
        props.hasDomains && props.selectedDomain
          ? `${props.values.email}${props.selectedDomain}`
          : props.values.email;
      dispatch(Creators.checkEmailAvailability(emailValue));
    } else {
      !props.isPhone ? props.completeSignUp() : handleNextStep();
    }
  };

  const onInputFocus = () => {
    dispatch(Creators.setSignUpStep(signUpSteps.EMAIL));
  };

  const handleDomainSelect = (domain) => {
    props.selectDomain(domain);
    domainsRef.current.focus();
  };

  return (
    <FormGroup onSubmit={onSubmit} className={`SignUp-form ${isCompleteProfileClass}`}>
      <div className="SignUp-form_email-container">
        <InputText
          {...{
            variant: `SignUp-form_email-input ${props.hasDomains ? "has-domains" : ""}`,
            errorMsg: _(props.showError(errorLocations.SIGN_UP_EMAIL)),
            id: "email",
            label: t`Email`,
            name: "email",
            onChange: (event) => {
              props.onEmailValueChange(event);
            },
            type: "text",
            validate: `${props.hasDomains ? "" : "email"}`,
            value: props.values.email,
            requiredLabel: "",
            additionalProps: {
              onFocus: onInputFocus,
            },
            ...(props.values.allowEmailEdit ? { isRequired: true } : { isDisabled: true }),
            ...(props.error && props.error.location === errorLocations.SIGN_UP_EMAIL
              ? { isError: true }
              : null),
          }}
        />
        {props.hasDomains && (
          <>
            <button
              id="domains-anchor"
              className={`SignUp-form_email-domains ${
                props.hasMultipleDomains ? "multiple-domains" : ""
              }`}
              onClick={() => {
                if (props.hasMultipleDomains) {
                  props.openDomainsFlyout();
                } else return;
              }}
              ref={domainsRef}
              type="button"
              aria-label={
                props.hasMultipleDomains
                  ? `Selected email domain for username is ${props.selectedDomain}. Click to change.`
                  : t`Email domain for username`
              }
            >
              <div className="SignUp-form_email-domain t-body-med">
                {props.selectedDomain}
                <input type="hidden" value={props.selectedDomain} name="selectedDomain" />
              </div>
              {props.hasMultipleDomains && <Icon icon="arrow_drop_down" />}
            </button>
            {props.hasMultipleDomains && (
              <Flyout
                anchorId="domains-anchor"
                alignment="bottomRight"
                isOpen={props.isDomainsFlyoutOpen}
                onClose={props.closeDomainsFlyout}
                closeOnClick={true}
                fixedPosition={true}
                defaultPadding={false}
                focusOnClose={domainsRef}
                focusOnOpen={`[aria-selected="true"]`}
                useTabTrapping={true}
              >
                <ul className="SignUp-form_email-domain-options">
                  {props.domains.map((domain) => (
                    <li
                      className="SignUp-form_email-domain-option t-body-med"
                      key={domain}
                      tabIndex={0}
                      role="option"
                      aria-selected={props.selectedDomain === domain}
                      onClick={() => handleDomainSelect(domain)}
                      onKeyUp={(evt) => {
                        if (evt.key === "Enter") {
                          handleDomainSelect(domain);
                        }
                      }}
                    >
                      {domain}
                    </li>
                  ))}
                </ul>
              </Flyout>
            )}
          </>
        )}
      </div>

      {!props.isPhone && (
        <>
          <div className="SignUp-form_names">
            <InputText
              type="text"
              label={t`First Name`}
              name="firstName"
              id="firstName"
              isRequired={true}
              onChange={props.onValueChange}
              value={props.values.firstName}
              requiredLabel={t`(required)`}
            />
            <InputText
              type="text"
              label={t`Last Name`}
              isRequired={true}
              name="lastName"
              id="lastName"
              onChange={props.onValueChange}
              value={props.values.lastName}
              requiredLabel={t`(required)`}
            />
          </div>
          <div className="SignUp-form_passwords">
            <div className="SignUp-form_pwContainer">
              <PasswordInput value={props.values.password} handleChange={props.onPasswordChange} />
            </div>
          </div>

          <Captcha
            doReveal={props.values.passwordStr >= PASSWORD_MIN_STR}
            onVerify={props.onCaptchaVerify}
          />
        </>
      )}

      <Button
        type="submit"
        className="SignUpWizard-nextSubmitBtn"
        isDisabled={props.isButtonDisabled(props.isPhone)}
      >
        <Trans>Sign Up</Trans>
      </Button>
    </FormGroup>
  );
}

export default Email;

// program key 200653
